/**
 * Смена сотрудника за день: шкала статусов и звонков, пропущенные, простои,
 * разбивка по часам, лента «Действия» — сообщения (скрипт CRM, «Чаты», расширение Chrome)
 * и правки сделок и лидов из «Чатов» и расширения. Спеки
 * `docs/specs/2026-09-24-activity-shift-design.md`,
 * `docs/specs/2026-09-24-activity-messenger-design.md` и
 * `docs/specs/2026-10-07-activity-own-actions-design.md`;
 * числа — те же, что у `prokat-runner activity-shift`.
 */
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { describeAction } from "@/components/chat/actionText";
import {
  type CallSpan,
  type CrmEdit,
  type GroupSend,
  loadShift,
  type MessageSend,
  type Messenger,
  type PanelEvent,
  type Shift,
} from "@/lib/activity";
import { loadStages, type UserAction } from "@/lib/dealStage";
import { workTime, yesterday } from "@/lib/leads";

const hhmm = (iso: string | null) => (iso ? iso.slice(11, 16) : "—");
/** 189 → «3,2 мин», 42 → «42 с». */
const пауза = (sec: number) => (sec < 60 ? `${sec} с` : `${(sec / 60).toFixed(1).replace(".", ",")} мин`);
const ms = (iso: string) => new Date(iso).getTime();

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

/** Цвет полосы статуса: работа — «ок», перерыв — предупреждение, ушёл — ошибка. Цвета темы, слов о них в подсказках нет. */
const STATUS_COLOR: Record<number, string> = {
  1: "bg-ok",
  2: "bg-warn",
  3: "bg-err",
  4: "bg-fg-faint",
  5: "bg-accent",
};

const CALL_COLOR: Record<CallSpan["kind"], string> = {
  out: "bg-accent",
  in: "bg-ok",
  other: "bg-fg-faint",
  unanswered: "bg-err",
};

const KIND_NAME: Record<CallSpan["kind"], string> = {
  out: "исходящий",
  in: "входящий",
  other: "прочий",
  unanswered: "пропущен",
};

const MESSENGER_NAME: Record<Messenger, string> = {
  wa: "WhatsApp",
  tg: "Telegram",
  max: "MAX",
  avito: "Авито",
  vk: "ВКонтакте",
  ig: "Instagram",
};
const MESSENGER_SHORT: Record<Messenger, string> = { wa: "WA", tg: "TG", max: "MAX", avito: "Авито", vk: "ВК", ig: "Инста" };

/** Откуда известно об отправке. */
const SOURCE: Record<MessageSend["source"], { name: string; hint: string }> = {
  chats: { name: "Чаты", hint: "отправлено из раздела «Чаты»" },
  ext: { name: "расширение", hint: "отправлено в браузере — заметило расширение Chrome" },
  crm: { name: "скрипт CRM", hint: "отправлено в браузере — записал только скрипт CRM, расширение не видело" },
};
const SCREEN: Record<string, string> = { chats: "Чаты", ext: "расширение" };

/** Действия в панели расширения — `activity/traces.ПАНЕЛЬ_SQL`. */
const PANEL_NAME: Record<PanelEvent["code"], string> = {
  peer: "открыл чат",
  lookup: "искал номер",
  click: "клик в панели",
  key: "ввод в панели",
  insert: "вставил текст",
};
const CHANNEL_SHORT: Record<string, string> = { whatsapp: "WA", telegram: "TG", max: "MAX", bitrix: "Bitrix" };
/** Поля истории CRM (`b24Deal_changes.fieldName`) по-человечески; `userIDmodify` — служебное «кто менял». */
const CRM_FIELD: Record<string, string> = {
  stageID: "стадия",
  statusID: "статус",
  status: "статус",
  utmContent: "метка рекламы",
  name: "имя",
  dateChangeStatus: "дата смены стадии",
  waitShowroom: "ждём в салоне",
  advertID: "источник",
  dealName: "название",
  typeID: "тип",
  leadID: "лид",
  contactID: "контакт",
  userIDresponsible: "ответственный",
  userIDcreated: "создал",
  ID: "создание",
  comments: "комментарий",
};
const crmFields = (f: string[]) => {
  if (f.includes("ID")) return "создание";
  const видимые = f.filter((x) => x !== "userIDmodify" && x !== "leadModified");
  return (видимые.length ? видимые : f).map((x) => CRM_FIELD[x] ?? x).join(", ");
};
const CHAT_OPEN = "chat_open";

/** «13:20» → минуты суток. */
const mins = (hm: string) => Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5));
const hm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/** Отрезок из «Контроля» (`?at=13:20-14:07`): журнал показывает его с запасом 15 минут по краям. */
export type Focus = { from: string; to: string };
const ЗАПАС = 15;

/** Шкала смены: шесть дорожек на общей оси времени от начала до конца смены. */
function Timeline({ s }: { s: Shift }) {
  if (!s.shift_from || !s.shift_to) return null;
  const a = ms(s.shift_from);
  const span = ms(s.shift_to) - a;
  const pos = (from: string, to: string) => ({
    left: `${((ms(from) - a) / span) * 100}%`,
    width: `max(2px, ${((ms(to) - ms(from)) / span) * 100}%)`,
  });

  const lanes: { name: string; hint: string; bars: ReactNode }[] = [
    {
      name: "Статусы",
      hint: "Работаю, Задача(В очереди), Задача(Без очереди), Перерыв, Ушел — цвета как в ленте статусов ниже",
      bars: s.statuses.map((st) => (
        <div
          key={st.from}
          className={`absolute inset-y-0 ${STATUS_COLOR[st.status_id] ?? "bg-fg-faint"} opacity-70`}
          style={pos(st.from, st.to)}
          title={`${st.name} ${hhmm(st.from)}–${hhmm(st.to)} · ${workTime(st.minutes)}${st.comment ? ` · ${st.comment}` : ""}`}
        />
      )),
    },
    {
      name: "Звонки",
      hint: "исходящие, принятые входящие (цвет «Работаю») и прочие — наведите на полосу",
      bars: s.calls.map((c) => (
        <div
          key={c.id}
          className={`absolute inset-y-0 ${CALL_COLOR[c.kind]}`}
          style={pos(c.from, c.to)}
          title={`${KIND_NAME[c.kind]} ${hhmm(c.from)}–${hhmm(c.to)} · разговор ${c.talk_sec} с`}
        />
      )),
    },
    {
      name: "Пропущено",
      hint: "непринятые входящие, пока сотрудник был на линии и свободен",
      bars: s.missed.map((m) => (
        <div
          key={m.id}
          className="absolute inset-y-0 bg-err"
          style={pos(m.from, m.to)}
          title={`пропущен ${hhmm(m.from)}–${hhmm(m.to)}`}
        />
      )),
    },
    {
      name: "Простой",
      hint: "от 10 минут без звонка в оплачиваемом статусе; минута после звонка — обработка, не простой",
      bars: s.idle.map((g) => (
        <div
          key={g.from}
          className="absolute inset-y-0 bg-warn-bg outline outline-1 outline-warn"
          style={pos(g.from, g.to)}
          title={`простой ${hhmm(g.from)}–${hhmm(g.to)} · ${workTime(g.minutes)} · пропущено ${g.missed}`}
        />
      )),
    },
    {
      name: "Сообщения",
      hint: "отправки клиентам: из «Чатов» и из браузера (расширение Chrome, скрипт CRM) — наведите на отметку",
      bars: s.messages.map((m) => (
        <div
          key={m.id}
          className="absolute inset-y-0 bg-accent"
          style={pos(m.at, m.at)}
          title={`${hhmm(m.at)} ${MESSENGER_NAME[m.messenger]} · ${m.account ?? "—"} → ${m.to ?? "получатель не записан"}${m.text ? ` · ${m.text.slice(0, 80)}` : ""}`}
        />
      )),
    },
    {
      name: "Действия",
      hint: "правки сделок и лидов, ссылки на запись, теги — из «Чатов» и панели расширения; наведите на отметку",
      bars: s.actions.map((x) => (
        <div
          key={x.id}
          className={`absolute inset-y-0 ${x.error ? "bg-err" : "bg-ok"}`}
          style={pos(x.at, x.at)}
          title={`${hhmm(x.at)} ${SCREEN[x.screen] ?? x.screen} · ${x.action}${x.deal_id ? ` · сделка ${x.deal_id}` : ""}${x.error ? ` · ${x.error}` : ""}`}
        />
      )),
    },
  ];

  // Час, начавшийся заметно раньше смены (09:00 при входе в 09:59), не подписываем:
  // его метка легла бы поверх следующей.
  const ticks = s.hours
    .map((h) => ({ hour: h.hour, t: ms(`${s.day}T${String(h.hour).padStart(2, "0")}:00:00`) }))
    .filter(({ t }) => t >= a - 5 * 60_000)
    .map(({ hour, t }) => ({ hour, left: Math.max(0, ((t - a) / span) * 100) }));

  return (
    <section
      className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot="шкала смены"
    >
      <div className="grid grid-cols-[90px_1fr] gap-y-2 text-[11.5px]">
        <div />
        <div className="relative h-4 text-fg-faint">
          {ticks.map((t) => (
            <span key={t.hour} className="absolute -translate-x-1/2 tabular-nums" style={{ left: `${t.left}%` }}>
              {t.hour}
            </span>
          ))}
        </div>
        {lanes.map((l) => (
          <div key={l.name} className="contents">
            <div className="cursor-help text-fg-muted" title={l.hint} data-annot={`дорожка «${l.name}»`}>
              {l.name}
            </div>
            <div className="relative h-5 overflow-hidden rounded-[3px] bg-bg">{l.bars}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[12px] text-fg-faint">Наведите на полосу — там время и подробности.</div>
    </section>
  );
}

const cell = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";
const head = "whitespace-nowrap px-2.5 py-2 text-right font-medium";

function Hours({ s }: { s: Shift }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="по часам">
      <div className="px-4 pt-3 text-[13px] font-medium">По часам</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-2 font-medium">Час</th>
              <th className={head}>Исход.</th>
              <th className={head} title="исходящие с разговором дольше 10 секунд">Дозвон</th>
              <th className={head}>Вход.</th>
              <th className={head} title="непринятые входящие, пока был на линии и свободен">Пропущено</th>
              <th className={head} title="разговоры + дозвон исходящих + 1 минута на каждый звонок (подготовка и обработка), по часу начала звонка">На звонках</th>
              <th className={head} title="простои от 10 минут, попавшие в этот час">Простой</th>
              <th className={head} title="статусы Работаю и Задача(В очереди)">На линии</th>
            </tr>
          </thead>
          <tbody>
            {s.hours.map((h) => {
              const dead = h.out + h.in === 0 && h.line_min > 30;
              return (
                <tr
                  key={h.hour}
                  className={`border-t border-border ${dead ? "bg-warn-bg" : ""}`}
                  data-annot={`час ${h.hour}`}
                >
                  <td className="px-4 py-1.5 tabular-nums">
                    {String(h.hour).padStart(2, "0")}:00
                  </td>
                  <td className={cell}>{h.out}</td>
                  <td className={cell}>{h.out_ok}</td>
                  <td className={cell}>{h.in}</td>
                  <td className={cell}>{h.missed ? <span className="text-err">{h.missed}</span> : 0}</td>
                  <td className={cell}>{workTime(h.busy_min)}</td>
                  <td className={cell}>{h.idle_min ? <span className="text-warn">{workTime(h.idle_min)}</span> : "—"}</td>
                  <td className={cell}>{workTime(h.line_min)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 pb-3 pt-1 text-[12px] text-fg-faint">
        Подсвечены часы, где больше получаса на линии и ни одного звонка.
      </div>
    </section>
  );
}

function Idle({ s }: { s: Shift }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="простои">
      <div className="px-4 pt-3 text-[13px] font-medium">Простои от 10 минут</div>
      {s.idle.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Простоев нет.</div>
      ) : (
        <table className="w-full text-[12.5px]">
          <tbody>
            {s.idle.map((g) => (
              <tr key={g.from} className="border-t border-border" data-annot={`простой ${hhmm(g.from)}`}>
                <td className="px-4 py-1.5 tabular-nums">
                  {hhmm(g.from)}–{hhmm(g.to)}
                </td>
                <td className={cell}>{workTime(g.minutes)}</td>
                <td className={cell}>
                  пропущено <span className={g.missed ? "text-err" : ""}>{g.missed}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

type FeedKind = "all" | "call" | "msg" | "grp" | "act" | "crm" | "panel";
type FeedRow = { key: string; at: string } & (
  | { msg: MessageSend }
  | { grp: GroupSend }
  | { act: UserAction }
  | { call: CallSpan }
  | { crm: CrmEdit }
  | { panel: PanelEvent }
  | { mark: "start" | "end" }
);

/** Сделка и чат строки ленты — переходы в карточку сделки и в «Чаты». */
function Refs({ dealId, chatId }: { dealId: number | null; chatId: number | null }) {
  if (!dealId && !chatId) return <span className="text-fg-faint">—</span>;
  return (
    <span className="flex gap-2">
      {dealId && (
        <Link to={`/deals/${dealId}`} className="text-accent hover:underline" title="карточка сделки">
          {dealId}
        </Link>
      )}
      {chatId && (
        <Link to={`/chats/${chatId}`} className="text-fg-muted hover:text-fg hover:underline" title="открыть диалог в «Чатах»">
          чат
        </Link>
      )}
    </span>
  );
}

/**
 * Лента «Действия»: всё, что сотрудник сделал руками за день, по времени — сообщения клиентам,
 * сообщения в рабочие группы и правки сделок и лидов (стадия, даты, город, мероприятие,
 * комментарий, ссылки, теги).
 */
function Actions({ s, focus, onWholeDay }: { s: Shift; focus?: Focus | null; onWholeDay?: () => void }) {
  const [kind, setKind] = useState<FeedKind>("all");
  const stages = useQuery({ queryKey: ["deal-stages"], queryFn: loadStages, staleTime: 60 * 60_000 });
  const stageName = (code: unknown) =>
    stages.data?.stages.find((x) => x.code === code)?.name ?? String(code ?? "—");
  const m = s.messenger;
  // «Открыл чат» — не правка: показываем рядом с действиями панели.
  const acts = s.actions.filter((x) => x.action !== CHAT_OPEN);
  const opens = s.actions.filter((x) => x.action === CHAT_OPEN);
  const crm = s.crm_edits ?? [];
  const panel = s.panel ?? [];
  const edits = acts.length;
  const failed = acts.filter((x) => x.error).length;
  // Журнал дня: звонки, сообщения и правки одной лентой — по ним и видно загрузку сотрудника.
  const calls: CallSpan[] = [...s.calls, ...s.missed];
  // Сообщения в группы — внутренняя переписка: своими строками, в счёт «Сообщений» не входят.
  const groups = s.group_messages ?? [];
  const all: FeedRow[] = [
    ...(kind === "all" || kind === "call" ? calls.map((call) => ({ key: `c${call.id}`, at: call.from, call })) : []),
    ...(kind === "all" || kind === "msg" ? s.messages.map((msg) => ({ key: `m${msg.id}`, at: msg.at, msg })) : []),
    ...(kind === "all" || kind === "grp" ? groups.map((grp) => ({ key: `g${grp.id}`, at: grp.at, grp })) : []),
    ...(kind === "all" || kind === "act" ? acts.map((act) => ({ key: `a${act.id}`, at: act.at, act })) : []),
    ...(kind === "all" || kind === "crm" ? crm.map((c) => ({ key: `r${c.kind}${c.id}${c.at}`, at: c.at, crm: c })) : []),
    ...(kind === "all" || kind === "panel"
      ? [
          ...panel.map((p) => ({ key: `p${p.id}`, at: p.at, panel: p })),
          ...opens.map((act) => ({ key: `a${act.id}`, at: act.at, act })),
        ]
      : []),
  ];
  // Границы отрезка — строками ленты; дата им не нужна: лента сортируется по времени дня.
  const marks: FeedRow[] = focus
    ? [
        { key: "mark-start", at: `0000-00-00T${focus.from}:00`, mark: "start" },
        { key: "mark-end", at: `0000-00-00T${focus.to}:00`, mark: "end" },
      ]
    : [];
  const rows = all
    .filter((r) => !focus || (mins(hhmm(r.at)) >= mins(focus.from) - ЗАПАС && mins(hhmm(r.at)) <= mins(focus.to) + ЗАПАС))
    .concat(marks)
    .sort((a, b) => a.at.slice(11).localeCompare(b.at.slice(11)));
  const tab = (k: FeedKind, label: string, n: number) => (
    <button
      type="button"
      onClick={() => setKind(k)}
      className={`rounded-[var(--radius-ui)] border px-2 py-0.5 text-[12px] tabular-nums ${
        kind === k ? "border-border-strong bg-bg text-fg" : "border-transparent text-fg-muted hover:text-fg"
      }`}
      data-annot={`фильтр действий «${label}»`}
    >
      {label} {n}
    </button>
  );
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="действия">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-3">
        <div className="text-[13px] font-medium">Журнал дня</div>
        <div className="flex gap-1">
          {tab("all", "Все", calls.length + m.sent + groups.length + edits + crm.length + panel.length + opens.length)}
          {tab("call", "Звонки", calls.length)}
          {tab("msg", "Сообщения", m.sent)}
          {tab("grp", "Группы", groups.length)}
          {tab("act", "Правки", edits)}
          {tab("crm", "CRM", crm.length)}
          {tab("panel", "Панель и чаты", panel.length + opens.length)}
        </div>
        <div className="text-[12px] text-fg-faint">
          всё, что сотрудник сделал за день, по времени: звонки, сообщения (из «Чатов» и из браузера —
          расширение Chrome, скрипт CRM), сообщения в рабочие группы, правки сделок и лидов в «Чатах» и в
          CRM, открытые чаты и действия в панели расширения
          {failed ? `; не прошло ${failed}` : ""}
          {m.dups ? `; двойных записей CRM отсеяно ${m.dups}` : ""}
        </div>
      </div>
      {focus && (
        <div className="mx-4 mt-2 flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-err bg-err-bg px-3 py-2 text-[12.5px] text-fg" data-annot="отрезок бездействия">
          <span>
            Отрезок <b>{focus.from}–{focus.to}</b>: здесь журнал за {hm(mins(focus.from) - ЗАПАС)}–{hm(mins(focus.to) + ЗАПАС)}. Между
            чертами — время, где «Контроль» не нашёл ни одного следа работы.
          </span>
          {onWholeDay && (
            <button type="button" onClick={onWholeDay} className="ml-auto h-7 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 text-[12px] hover:bg-bg-hover">
              Весь день
            </button>
          )}
        </div>
      )}
      {s.messages.length + groups.length + s.actions.length + crm.length + panel.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Ни сообщений, ни правок за день нет.</div>
      ) : (
        <>
          {s.messenger_accounts.length > 0 && (
            <table className="mt-2 w-full text-[12.5px]" data-annot="сообщения по аккаунтам">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-2 font-medium">Мессенджер</th>
                  <th className="px-2.5 py-2 font-medium" title="«Чаты» — наш официальный вход канала; иначе — аккаунт в браузере">
                    Наш аккаунт
                  </th>
                  <th className={head}>Отправок</th>
                  <th className={head} title="разных получателей; прочерк — получателя никто не записал">
                    Получателей
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.messenger_accounts.map((a) => (
                  <tr key={`${a.messenger}-${a.account}`} className="border-t border-border">
                    <td className="px-4 py-1.5">{MESSENGER_NAME[a.messenger]}</td>
                    <td className="px-2.5 py-1.5">{a.account ?? "—"}</td>
                    <td className={cell}>{a.sent}</td>
                    <td className={cell}>
                      {a.recipients ?? <span className="text-fg-faint" title="получателя никто не записал">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="max-h-[640px] overflow-auto border-t border-border-strong">
            <table className="w-full text-[12.5px]" data-annot="лента действий">
              <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-2 font-medium">Время</th>
                  <th className="px-2.5 py-2 font-medium" title="где сделано: раздел «Чаты», расширение Chrome или скрипт CRM в браузере">
                    Где
                  </th>
                  <th className="px-2.5 py-2 font-medium">Что</th>
                  <th className="px-2.5 py-2 font-medium">Кому</th>
                  <th className="px-2.5 py-2 font-medium">Подробности</th>
                  <th className="px-2.5 py-2 font-medium">Сделка</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) =>
                  "call" in r ? (
                    <tr key={r.key} className="border-t border-border align-top" data-annot={`звонок ${r.call.id}`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{hhmm(r.at)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted" title="телефония Bitrix">
                        звонок
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">
                        <span className={r.call.kind === "unanswered" ? "text-err" : ""}>
                          {KIND_NAME[r.call.kind]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        {r.call.phone ? (
                          <Link to={`/search?q=${r.call.phone}`} className="hover:underline" title="поиск по номеру">
                            {r.call.phone}
                          </Link>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                      </td>
                      <td className="px-2.5 py-1.5 text-fg-muted">
                        {r.call.talk_sec > 0 ? (
                          `разговор ${пауза(r.call.talk_sec)}`
                        ) : (
                          <span className="text-fg-faint">без разговора</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        <span className="flex gap-2">
                          {r.call.deal_id && (
                            <Link to={`/deals/${r.call.deal_id}`} className="text-accent hover:underline" title="карточка сделки">
                              {r.call.deal_id}
                            </Link>
                          )}
                          {r.call.lead_id && (
                            <Link to={`/leads/${r.call.lead_id}`} className="text-fg-muted hover:text-fg hover:underline" title="карточка лида">
                              лид {r.call.lead_id}
                            </Link>
                          )}
                          {!r.call.deal_id && !r.call.lead_id && <span className="text-fg-faint">—</span>}
                        </span>
                      </td>
                    </tr>
                  ) : "grp" in r ? (
                    <tr key={r.key} className="border-t border-border align-top" data-annot={`сообщение в группу ${r.grp.id}`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{hhmm(r.at)}</td>
                      <td
                        className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted"
                        title="групповой чат — внутренняя переписка, в «Сообщения» клиентам не входит"
                      >
                        группа
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">
                        в группу <span className="text-fg-faint">{MESSENGER_SHORT[r.grp.messenger]}</span>
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">{r.grp.to ?? <span className="text-fg-faint">—</span>}</td>
                      <td className="px-2.5 py-1.5 text-fg-muted">
                        {r.grp.text ?? <span className="text-fg-faint">без текста</span>}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        <Refs dealId={null} chatId={r.grp.chat_id} />
                      </td>
                    </tr>
                  ) : "mark" in r ? (
                    <tr key={r.key} className="border-t-2 border-err bg-err-bg" data-annot={`граница отрезка ${r.mark}`}>
                      <td className="whitespace-nowrap px-4 py-1 font-medium tabular-nums text-err">{hhmm(r.at)}</td>
                      <td className="px-2.5 py-1 text-err" colSpan={5}>
                        {r.mark === "start" ? "начало отрезка без следов работы" : "конец отрезка без следов работы"}
                      </td>
                    </tr>
                  ) : "crm" in r ? (
                    <tr key={r.key} className="border-t border-border align-top" data-annot={`правка CRM ${r.crm.id}`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{hhmm(r.at)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted" title="история изменений CRM (b24Deal_changes, b24Lead_changes)">
                        CRM
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">{r.crm.kind === "deal" ? "правка сделки" : "правка лида"}</td>
                      <td className="px-2.5 py-1.5 text-fg-faint">—</td>
                      <td className="px-2.5 py-1.5 text-fg-muted">{crmFields(r.crm.fields)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        {r.crm.kind === "deal" ? (
                          <Refs dealId={r.crm.id} chatId={null} />
                        ) : (
                          <Link to={`/leads/${r.crm.id}`} className="text-fg-muted hover:text-fg hover:underline" title="карточка лида">
                            лид {r.crm.id}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ) : "panel" in r ? (
                    <tr key={r.key} className="border-t border-border align-top" data-annot={`панель ${r.panel.id}`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{hhmm(r.at)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted" title="панель расширения Chrome">
                        расширение
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">
                        {PANEL_NAME[r.panel.code] ?? r.panel.code}{" "}
                        <span className="text-fg-faint">{CHANNEL_SHORT[r.panel.channel] ?? r.panel.channel}</span>
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        {r.panel.name || r.panel.phone || <span className="text-fg-faint">—</span>}
                      </td>
                      <td className="px-2.5 py-1.5 text-fg-faint">—</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        <Refs dealId={r.panel.deal_id} chatId={r.panel.chat_id} />
                      </td>
                    </tr>
                  ) : "msg" in r ? (
                    <tr key={r.key} className="border-t border-border align-top" data-annot={`сообщение ${r.msg.id}`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{hhmm(r.at)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted" title={SOURCE[r.msg.source].hint}>
                        {SOURCE[r.msg.source].name}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5">
                        сообщение <span className="text-fg-faint">{MESSENGER_SHORT[r.msg.messenger]}</span>
                        {r.msg.source !== "chats" && r.msg.account ? ` ${r.msg.account}` : ""}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        {r.msg.to ?? <span className="text-fg-faint" title="получателя никто не записал">—</span>}
                      </td>
                      <td className="px-2.5 py-1.5 text-fg-muted">
                        {r.msg.text ?? (
                          <span className="text-fg-faint" title="расширение пишет только факт отправки и длину">
                            {r.msg.len ? `текст не сохранён, ${r.msg.len} зн.` : "без текста"}
                          </span>
                        )}
                        {r.msg.dups > 0 && (
                          <span className="ml-1.5 text-fg-faint" title="столько записей CRM схлопнуто в эту отправку">
                            ×{r.msg.dups + 1}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        <Refs dealId={r.msg.deal_id} chatId={r.msg.chat_id} />
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={r.key}
                      className={`border-t border-border align-top ${r.act.error ? "bg-err-bg" : "bg-bg"}`}
                      data-annot={`действие ${r.act.id}`}
                    >
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{hhmm(r.at)}</td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted">
                        {SCREEN[r.act.screen] ?? r.act.screen}
                      </td>
                      <td className="px-2.5 py-1.5 font-medium" colSpan={3}>
                        {describeAction(r.act, stageName)}
                        {r.act.error && <span className="ml-1.5 font-normal text-err">не прошло: {r.act.error}</span>}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">
                        <Refs dealId={r.act.deal_id} chatId={r.act.chat_id} />
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Statuses({ s }: { s: Shift }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="статусы">
      <div className="px-4 pt-3 text-[13px] font-medium">Статусы</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-2 font-medium">Время</th>
              <th className="px-2.5 py-2 font-medium">Статус</th>
              <th className={head}>Длит.</th>
              <th className="px-2.5 py-2 font-medium">Комментарий</th>
              <th className="px-2.5 py-2 font-medium" title="IP и отпечаток устройства (userAgent в CRM), с которых ставили статус">Откуда</th>
            </tr>
          </thead>
          <tbody>
            {s.statuses.map((st) => (
              <tr key={st.from} className="border-t border-border" data-annot={`статус ${hhmm(st.from)}`}>
                <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                  {hhmm(st.from)}–{hhmm(st.to)}
                </td>
                <td className="whitespace-nowrap px-2.5 py-1.5">
                  <span className={`mr-1.5 inline-block size-2 rounded-full ${STATUS_COLOR[st.status_id] ?? "bg-fg-faint"}`} />
                  {st.name}
                </td>
                <td className={cell}>{workTime(st.minutes)}</td>
                <td className="px-2.5 py-1.5 text-fg-muted">{st.comment || "—"}</td>
                <td className="px-2.5 py-1.5 text-fg-muted">
                  {[st.ip, st.agent].filter(Boolean).join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Body({ s, focus, onWholeDay }: { s: Shift; focus: Focus | null; onWholeDay: () => void }) {
  const t = s.totals;
  if (!s.shift_from) {
    return (
      <>
        <div className="text-[12.5px] text-fg-muted">В этот день у сотрудника не было смены.</div>
        {s.messages.length + (s.group_messages ?? []).length + s.actions.length > 0 && (
          <Actions s={s} focus={focus} onWholeDay={onWholeDay} />
        )}
      </>
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Tile
          label="Смена"
          value={`${hhmm(s.shift_from)}–${hhmm(s.shift_to)}`}
          hint={`часов ${workTime(s.paid_min)}, на линии ${workTime(s.line_min)}`}
        />
        <Tile
          label="Звонков"
          value={t.out + t.in + t.other}
          hint={`исходящих ${t.out} (дозвон ${t.out_ok}), входящих ${t.in}; на звонках ${workTime(t.busy_min)}`}
        />
        <Tile label="Пропущено" value={t.missed} hint="входящих, пока был на линии и свободен" />
        <Tile
          label="Простой"
          value={workTime(t.idle_min)}
          hint={`${s.idle.length} отрезков от 10 минут без звонка`}
        />
        <Tile
          label="Сообщений"
          value={s.messenger.sent}
          hint={`WhatsApp ${s.messenger.wa}, Telegram ${s.messenger.tg}, MAX ${s.messenger.max}, Авито ${s.messenger.avito}, ВК ${s.messenger.vk}, Instagram ${s.messenger.ig}`}
        />
        <Tile
          label="Правок"
          value={s.actions.filter((x) => x.action !== CHAT_OPEN).length}
          hint="сделки и лиды из «Чатов» и расширения: стадия, даты, город, ссылки, теги"
        />
      </div>
      <Timeline s={s} />
      <Hours s={s} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <Idle s={s} />
        <Statuses s={s} />
      </div>
      <Actions s={s} focus={focus} onWholeDay={onWholeDay} />
    </>
  );
}

export function ShiftPage() {
  const user = Number(useParams().userId);
  const [params, setParams] = useSearchParams();
  const day = params.get("date") || yesterday();
  const at = /^(\d\d:\d\d)-(\d\d:\d\d)$/.exec(params.get("at") ?? "");
  const focus: Focus | null = at ? { from: at[1]!, to: at[2]! } : null;
  const shift = useQuery({
    queryKey: ["activity-shift", user, day],
    queryFn: () => loadShift(user, day),
    enabled: Number.isInteger(user),
  });

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка смены">
        <Link to={`/activity?date=${day}`} className="text-[12.5px] text-fg-muted hover:text-fg">
          ← Активность
        </Link>
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">
          {shift.data ? shift.data.name : `Сотрудник ${user}`}
        </h1>
        {shift.data && <span className="text-[12.5px] text-fg-faint">{shift.data.location}</span>}
        <label className="ml-auto flex items-center gap-2 text-[12.5px] text-fg-muted">
          День
          <input
            type="date"
            value={day}
            max={new Date().toLocaleDateString("sv-SE")}
            onChange={(e) => e.target.value && setParams({ date: e.target.value })}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
          />
        </label>
      </div>
      {shift.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
      ) : shift.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить смену: {String(shift.error.message)}</div>
      ) : (
        <Body s={shift.data} focus={focus} onWholeDay={() => setParams({ date: day })} />
      )}
    </main>
  );
}
